import React, { useState } from 'react';
import { Drawer } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  FormatListBulleted,
  ListAlt,
  Menu,
  ChevronLeft,
  ChevronRight,
  RecentActors,
  PlaylistAddCheck,
  Comment,
  ManageSearch,
} from '@mui/icons-material';
import { SIDEBAR_TEXT } from '../lang';
import { SUPERVISOR_MONITOR_LIST_MODULE } from '../constant';
import { type SidebarData, Sidebar, IconButton } from '../../../../components';
import { useAuth } from '../../../../contexts';

const drawerWidth = 270;
export type SupervisorMonitorSidebarProps = {
  collapsible?: boolean;
  onChangeActivated: (activatedId: SUPERVISOR_MONITOR_LIST_MODULE) => void;
  activatedModule?: SUPERVISOR_MONITOR_LIST_MODULE;
};

const delegationModeOn = process.env.REACT_APP_DELEGATION_ON === 'true';
export const SupervisorMonitorSideBar: React.FunctionComponent<
  SupervisorMonitorSidebarProps
> = (props: SupervisorMonitorSidebarProps) => {
  const { user } = useAuth();
  const delegateMode = user?.sub !== user?.userInfo?.netId && delegationModeOn;

  const sidebarData: SidebarData[] = [
    {
      id: SUPERVISOR_MONITOR_LIST_MODULE.APPLICATION_LIST,
      name: SIDEBAR_TEXT.APPLICATION_LIST,
      icon: <FormatListBulleted />,
    },
    {
      id: SUPERVISOR_MONITOR_LIST_MODULE.NON_PROPOSED_APPLICATION,
      name: SIDEBAR_TEXT.NON_PROPOSED_APPLICATION,
      icon: <ListAlt />,
    },
    {
      id: SUPERVISOR_MONITOR_LIST_MODULE.CO_SUPERVISOR_DECLARATION,
      name: SIDEBAR_TEXT.CO_SUPERVISOR_DECLARATION,
      icon: <PlaylistAddCheck />,
    },
    {
      id: SUPERVISOR_MONITOR_LIST_MODULE.POLISH_COMMENTS,
      name: SIDEBAR_TEXT.SHORTLISTED_HKPFS,
      icon: <Comment />,
    },
    {
      id: SUPERVISOR_MONITOR_LIST_MODULE.SCHOLARSHIP_MANAGEMENT,
      name: SIDEBAR_TEXT.SCHOLARSHIP_MANAGEMENT,
      icon: <ManageSearch />,
    },

    ...(delegateMode
      ? [
          {
            id: SUPERVISOR_MONITOR_LIST_MODULE.DELEGATE_LIST,
            name: SIDEBAR_TEXT.DELEGATE_LIST,
            icon: <RecentActors />,
          },
        ]
      : []),
  ];

  const { onChangeActivated, collapsible = false, activatedModule } = props;
  const [open, setOpen] = useState(true);
  const [defaultActivatedModule, setDefaultActivatedModule] = useState(
    activatedModule ?? SUPERVISOR_MONITOR_LIST_MODULE.TO_BE_NOMINATED,
  );

  const drawerContentStyle: React.CSSProperties = {
    transition: 'padding-top 0.25s ease',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    scrollbarWidth: 'thin',
    position: 'sticky',
  };

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 3),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  }));

  const theme = useTheme();

  const handleDrawerOpen = (): void => {
    setOpen(true);
  };

  const handleDrawerClose = (): void => {
    setOpen(false);
  };

  const handleChangeActivated = (activatedId: string): void => {
    onChangeActivated(activatedId as SUPERVISOR_MONITOR_LIST_MODULE);
    setDefaultActivatedModule(activatedId as SUPERVISOR_MONITOR_LIST_MODULE);
  };

  return collapsible ? (
    <>
      <div
        style={{
          position: 'relative',
          top: '1rem',
          left: '1.25rem',
          width: '2.25rem',
        }}
      >
        <IconButton
          color="default"
          aria-label="open drawer"
          onClick={handleDrawerOpen}
          edge="start"
          sx={{ mr: 2, ...(open && { display: 'none' }), position: 'fixed' }}
          icon={<Menu />}
        />
      </div>
      <Drawer
        style={{
          top: '5rem',
          position: 'sticky',
          maxHeight: '100vh',
          height: '100%',
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
          '& .sc-jTrNMu': {
            width: '14rem',
          },
          '& .MuiPaper-root': {
            position: 'sticky',
            height: 'calc(100vh - 5rem)',
            top: '5rem',
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          flexGrow: 1,
          ...(!open && { display: 'none' }),
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <div style={drawerContentStyle}>
          <DrawerHeader>
            <IconButton
              onClick={handleDrawerClose}
              style={{ marginBottom: '-1.25rem' }}
              icon={
                theme.direction === 'ltr' ? <ChevronLeft /> : <ChevronRight />
              }
            />
          </DrawerHeader>
          <Sidebar
            defaultActivatedId={defaultActivatedModule}
            sidebarData={sidebarData}
            onChangeActivated={handleChangeActivated}
            barInnerWidth="16.5rem"
          />
        </div>
      </Drawer>
    </>
  ) : (
    <Sidebar
      defaultActivatedId={defaultActivatedModule}
      sidebarData={sidebarData}
      onChangeActivated={handleChangeActivated}
    />
  );
};
